// =============================================================================
// OFFLINE STORAGE - IndexedDB pour le mode hors-ligne du DAL
// =============================================================================

const DB_NAME = 'omnihub_dal_offline';
const DB_VERSION = 1;
const STORE_DAL_DATA = 'dal_data';
const STORE_PENDING_SYNC = 'pending_sync';
const STORE_PHOTOS = 'photos';
// N°546 — état d'un élément de file refusé par le serveur (409 / 428).
export const ETAT_A_REVOIR = 'a_revoir';

class OfflineStorage {
  constructor() {
    this.db = null;
    this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.syncInProgress = false;
    
    try {
      if (typeof window !== 'undefined') {
        window.addEventListener('online', () => this.handleOnline());
        window.addEventListener('offline', () => this.handleOffline());
      }
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data && event.data.type === 'TRIGGER_SYNC') {
            this.syncPendingData();
          }
        });
      }
    } catch (e) {
      // Silently handle environments where these APIs aren't available
    }
  }

  // Initialiser la base de données
  async init() {
    if (this.db) return this.db;
    
    return new Promise((resolve, reject) => {
      try {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        
        request.onerror = () => {
          console.error('[OfflineStorage] Erreur ouverture DB:', request.error);
          // En cas d'erreur, on continue sans IndexedDB
          resolve(null);
        };
        
        request.onsuccess = () => {
          this.db = request.result;
          resolve(this.db);
        };
        
        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          
          // Store pour les données DAL
          if (!db.objectStoreNames.contains(STORE_DAL_DATA)) {
            const dalStore = db.createObjectStore(STORE_DAL_DATA, { keyPath: 'dossierId' });
            dalStore.createIndex('tenant', 'tenantId', { unique: false });
            dalStore.createIndex('lastModified', 'lastModified', { unique: false });
          }
          
          // Store pour les données en attente de synchronisation
          if (!db.objectStoreNames.contains(STORE_PENDING_SYNC)) {
            const syncStore = db.createObjectStore(STORE_PENDING_SYNC, { keyPath: 'id', autoIncrement: true });
            syncStore.createIndex('dossierId', 'dossierId', { unique: false });
            syncStore.createIndex('timestamp', 'timestamp', { unique: false });
          }
          
          // Store pour les photos
          if (!db.objectStoreNames.contains(STORE_PHOTOS)) {
            const photoStore = db.createObjectStore(STORE_PHOTOS, { keyPath: 'id' });
            photoStore.createIndex('dossierId', 'dossierId', { unique: false });
          }
        };
      } catch (err) {
        console.error('[OfflineStorage] Erreur init:', err);
        resolve(null);
      }
    });
  }

  // Sauvegarder les données DAL localement
  async saveDALData(dossierId, tenantId, data) {
    if (!dossierId) {
      console.warn('[OfflineStorage] saveDALData ignoré: dossierId invalide');
      return null;
    }
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([STORE_DAL_DATA], 'readwrite');
        const store = transaction.objectStore(STORE_DAL_DATA);
        
        const record = {
          dossierId: String(dossierId),
          tenantId: tenantId || '',
          data,
          lastModified: new Date().toISOString(),
          synced: false
        };
        
        const request = store.put(record);
        request.onsuccess = () => resolve(record);
        request.onerror = () => {
          console.error('[OfflineStorage] Erreur put DAL:', request.error);
          resolve(null);
        };
      } catch (err) {
        console.error('[OfflineStorage] Erreur saveDALData:', err);
        resolve(null);
      }
    });
  }

  // Récupérer les données DAL locales
  async getDALData(dossierId) {
    if (!dossierId) return null;
    if (!this.db) await this.init();
    if (!this.db) return null;
    
    return new Promise((resolve) => {
      try {
        const transaction = this.db.transaction([STORE_DAL_DATA], 'readonly');
        const store = transaction.objectStore(STORE_DAL_DATA);
        const request = store.get(String(dossierId));
        
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
      } catch (err) {
        resolve(null);
      }
    });
  }

  // Ajouter une opération en attente de synchronisation
  async addPendingSync(dossierId, tenantId, operation, data) {
    if (!dossierId) {
      console.warn('[OfflineStorage] addPendingSync ignoré: dossierId invalide');
      return null;
    }
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([STORE_PENDING_SYNC], 'readwrite');
        const store = transaction.objectStore(STORE_PENDING_SYNC);
        
        const record = {
          dossierId: String(dossierId),
          tenantId: tenantId || '',
          operation,
          data,
          timestamp: new Date().toISOString(),
          retries: 0
        };
        
        const request = store.add(record);
        request.onsuccess = () => resolve(record);
        request.onerror = () => {
          console.error('[OfflineStorage] Erreur add sync:', request.error);
          resolve(null);
        };
      } catch (err) {
        console.error('[OfflineStorage] Erreur addPendingSync:', err);
        resolve(null);
      }
    });
  }

  // Récupérer les opérations en attente
  async getPendingSync() {
    if (!this.db) await this.init();
    if (!this.db) return [];
    
    return new Promise((resolve) => {
      try {
        const transaction = this.db.transaction([STORE_PENDING_SYNC], 'readonly');
        const store = transaction.objectStore(STORE_PENDING_SYNC);
        const request = store.getAll();
        
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => resolve([]);
      } catch (err) {
        resolve([]);
      }
    });
  }

  // Supprimer une opération synchronisée
  async removePendingSync(id) {
    if (!this.db) await this.init();
    if (!this.db) return;
    
    return new Promise((resolve) => {
      try {
        const transaction = this.db.transaction([STORE_PENDING_SYNC], 'readwrite');
        const store = transaction.objectStore(STORE_PENDING_SYNC);
        const request = store.delete(id);
        
        request.onsuccess = () => resolve();
        request.onerror = () => resolve();
      } catch (err) {
        resolve();
      }
    });
  }

  // Sauvegarder une photo localement
  async savePhoto(dossierId, photoId, zone, blob) {
    if (!photoId) {
      console.warn('[OfflineStorage] savePhoto ignoré: photoId invalide');
      return null;
    }
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([STORE_PHOTOS], 'readwrite');
        const store = transaction.objectStore(STORE_PHOTOS);
        
        const record = {
          id: String(photoId),
          dossierId: String(dossierId || ''),
          zone,
          blob,
          timestamp: new Date().toISOString(),
          synced: false
        };
        
        const request = store.put(record);
        request.onsuccess = () => resolve(record);
        request.onerror = () => {
          console.error('[OfflineStorage] Erreur put photo:', request.error);
          resolve(null);
        };
      } catch (err) {
        console.error('[OfflineStorage] Erreur savePhoto:', err);
        resolve(null);
      }
    });
  }

  // Récupérer les photos d'un dossier
  async getPhotos(dossierId) {
    if (!this.db) await this.init();
    if (!this.db) return [];
    
    return new Promise((resolve) => {
      try {
        const transaction = this.db.transaction([STORE_PHOTOS], 'readonly');
        const store = transaction.objectStore(STORE_PHOTOS);
        const index = store.index('dossierId');
        const request = index.getAll(String(dossierId || ''));
        
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => resolve([]);
      } catch (err) {
        resolve([]);
      }
    });
  }

  // Synchroniser les données en attente
  async syncPendingData() {
    if (this.syncInProgress || !navigator.onLine) return;
    
    this.syncInProgress = true;
    console.log('[OfflineStorage] Début de la synchronisation...');
    
    try {
      const pending = await this.getPendingSync();

      for (const item of pending) {
        // 🔴 N°546 — une saisie REFUSÉE par le serveur (409/428) attend une
        // résolution HUMAINE : on ⛔ ne la rejoue plus, on ⛔ ne la supprime pas.
        if (item.etat === ETAT_A_REVOIR) continue;
        try {
          const API = process.env.REACT_APP_BACKEND_URL || '';

          if (item.operation === 'save_dal') {
            const mobileToken = localStorage.getItem('mobile_dal_token') || '';
            const response = await fetch(`${API}/api/dal/save-mobile/${item.dossierId}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Tenant-ID': item.tenantId,
                ...(mobileToken && { 'Authorization': `Bearer ${mobileToken}` })
              },
              body: JSON.stringify(item.data)
            });

            if (response.ok) {
              await this.removePendingSync(item.id);
              console.log(`[OfflineStorage] Synchronisé: ${item.dossierId}`);
            } else if (response.status === 409 || response.status === 428) {
              // 🔴 N°546 — REFUS SERVEUR ≠ SUPPRESSION : l'élément devient un
              // ÉTAT MÉTIER VISIBLE, données locales CONSERVÉES.
              let motif = `${response.status}`;
              try {
                const j = await response.json();
                motif = (j?.detail && (j.detail.message || j.detail.code)) || j?.detail || motif;
              } catch { /* corps illisible : le code HTTP suffit */ }
              await this.marquerARevoir(item, response.status, motif);
            }
          }
          
          if (item.operation === 'upload_photo') {
            const formData = new FormData();
            formData.append('file', item.data.blob, item.data.filename);
            formData.append('zone', item.data.zone);
            const mobileToken = localStorage.getItem('mobile_dal_token') || '';
            
            const response = await fetch(`${API}/api/dal/upload-photo/${item.dossierId}`, {
              method: 'POST',
              headers: {
                'X-Tenant-ID': item.tenantId,
                ...(mobileToken && { 'Authorization': `Bearer ${mobileToken}` })
              },
              body: formData
            });
            
            if (response.ok) {
              await this.removePendingSync(item.id);
              console.log(`[OfflineStorage] Photo synchronisée: ${item.dossierId}`);
            }
          }
        } catch (error) {
          console.error(`[OfflineStorage] Erreur sync item ${item.id}:`, error);
        }
      }
      
      // Marquer les données DAL comme synchronisées
      const dalData = await this.getAllDALData();
      for (const dal of dalData) {
        if (!dal.synced && dal.dossierId) {
          dal.synced = true;
          await this.saveDALData(dal.dossierId, dal.tenantId, dal.data);
        }
      }
      
    } catch (error) {
      console.error('[OfflineStorage] Erreur synchronisation:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  // Récupérer toutes les données DAL
  async getAllDALData() {
    if (!this.db) await this.init();
    if (!this.db) return [];
    
    return new Promise((resolve) => {
      try {
        const transaction = this.db.transaction([STORE_DAL_DATA], 'readonly');
        const store = transaction.objectStore(STORE_DAL_DATA);
        const request = store.getAll();
        
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => resolve([]);
      } catch (err) {
        resolve([]);
      }
    });
  }

  // Gestionnaires de connexion
  handleOnline() {
    this.isOnline = true;
    console.log('[OfflineStorage] Connexion rétablie, synchronisation...');
    this.syncPendingData();
  }

  handleOffline() {
    this.isOnline = false;
    console.log('[OfflineStorage] Mode hors-ligne activé');
  }

  // Vérifier s'il y a des données non synchronisées
  async hasPendingData() {
    const pending = await this.getPendingSync();
    return pending.length > 0;
  }

  // Obtenir le nombre d'éléments en attente (⛔ hors « à revoir » : ceux-là ne
  // partiront pas tout seuls, ils sont comptés à part — N°546)
  async getPendingCount() {
    const pending = await this.getPendingSync();
    return pending.filter(i => i.etat !== ETAT_A_REVOIR).length;
  }

  // 🔴 N°546 — les saisies hors ligne REFUSÉES par le serveur (conflit de
  // version 409, révision absente 428). Données locales intactes.
  async getARevoir() {
    const pending = await this.getPendingSync();
    return pending.filter(i => i.etat === ETAT_A_REVOIR);
  }

  // Passe un élément en « à revoir » : ⛔ jamais supprimé, ⛔ plus rejoué.
  async marquerARevoir(item, httpStatus, motif) {
    if (!this.db) await this.init();
    if (!this.db || !item) return;
    return new Promise((resolve) => {
      try {
        const transaction = this.db.transaction([STORE_PENDING_SYNC], 'readwrite');
        const store = transaction.objectStore(STORE_PENDING_SYNC);
        const request = store.put({
          ...item,
          etat: ETAT_A_REVOIR,
          refus: { http: httpStatus, motif: String(motif || ''), le: new Date().toISOString() },
        });
        request.onsuccess = () => resolve();
        request.onerror = () => resolve();
      } catch (err) {
        resolve();
      }
    });
  }
}

// Instance singleton
const offlineStorage = new OfflineStorage();

export default offlineStorage;
